// src/utils/product-converter.js

const PRODUCT_MODEL = {
  id: "",
  sku: "",
  name: "",
  price: 0,
  discount: 0,
  offerEnd: "",
  new: false,
  rating: 0,
  saleCount: 0,
  category: [""],
  tag: [""],
  brand: [""],
  variation: [],
  image: [],
  shortDescription: "",
  fullDescription: "",
  stock: 50,
};

const VARIATION_MODEL = {
  color: "",
  image: "",
  size: [],
};

const SIZE_MODEL = {
  name: "",
  stock: 50,
};

const DEFAULT_IMAGE = "/assets/img/product/placeholder.jpg";

const isObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const ensureString = (value) => {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
};

const ensureNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isNaN(num) ? fallback : num;
};

const ensureBoolean = (value, fallback = false) => {
  if (typeof value === "boolean") return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return fallback;
};

const ensureArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value == null || value === "") return [];
  return [value];
};

const getReferenceId = (value) => {
  if (value == null) return "";

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  if (typeof value === "object") {
    return String(value.id ?? value.value ?? value._id ?? value.key ?? "");
  }

  return "";
};

const normalizeImageArray = (value) => {
  const arr = ensureArray(value);

  const normalized = arr
    .map((item) => {
      if (item == null) return "";
      if (typeof item === "string") return item;

      if (isObject(item)) {
        return ensureString(
          item.url || item.src || item.path || item.image || item.imagem
        );
      }

      return "";
    })
    .filter(Boolean);

  return normalized.length ? normalized : [DEFAULT_IMAGE];
};

const normalizeSizes = (sizes) => {
  const arr = ensureArray(sizes);

  if (!arr.length) {
    return [{ ...SIZE_MODEL }];
  }

  return arr.map((size) => {
    if (!isObject(size)) {
      return { ...SIZE_MODEL };
    }

    return {
      ...SIZE_MODEL,
      name: ensureString(size.name ?? size.nome),
      stock: 50,
    };
  });
};

const normalizeVariations = (
  variations,
  brandName = "",
  fallbackImage = DEFAULT_IMAGE
) => {
  const arr = ensureArray(variations);

  if (!arr.length) {
    return [
      {
        ...VARIATION_MODEL,
        color: brandName,
        image: fallbackImage,
        size: [{ ...SIZE_MODEL }],
      },
    ];
  }

  return arr.map((variation) => {
    if (!isObject(variation)) {
      return {
        ...VARIATION_MODEL,
        color: brandName,
        image: fallbackImage,
        size: [{ ...SIZE_MODEL }],
      };
    }

    return {
      ...VARIATION_MODEL,
      color: brandName,
      image:
        ensureString(
          variation.image ??
            variation.imagem ??
            variation.img ??
            variation.foto
        ) || fallbackImage,
      size: normalizeSizes(
        variation.size ?? variation.sizes ?? variation.tamanhos
      ),
    };
  });
};

export const convertFirebaseProduct = (
  product = {},
  { categorias = [], marcas = [], tags = [] } = {}
) => {
  const safeProduct = isObject(product) ? product : {};

  const categoriaId = getReferenceId(safeProduct.categoriaId);
  const marcaId = getReferenceId(safeProduct.marcaId);
  const tagIds = ensureArray(safeProduct.tagIds).map(getReferenceId);

  const categoryName =
    categorias.find((item) => getReferenceId(item.id) === categoriaId)?.name || "";

  const brandName =
    marcas.find((item) => getReferenceId(item.id) === marcaId)?.name || "";

  const tagNames = tagIds
    .map((tagId) => tags.find((item) => getReferenceId(item.id) === tagId)?.name || "")
    .filter(Boolean);

  const normalizedImages = normalizeImageArray(
    safeProduct.image ??
      safeProduct.imagem ??
      safeProduct.imagens ??
      safeProduct.fotos
  );

  return {
    ...PRODUCT_MODEL,
    id: ensureString(safeProduct.id),
    sku: ensureString(
      safeProduct.sku ??
        safeProduct.codigo ??
        safeProduct.idNome ??
        safeProduct.codigoProduto
    ),
    name: ensureString(
      safeProduct.name ?? safeProduct.nome ?? safeProduct.titulo
    ),
    price: ensureNumber(
      safeProduct.price ?? safeProduct.preco ?? safeProduct.valor,
      0
    ),
    discount: ensureNumber(safeProduct.discount ?? safeProduct.desconto, 0),
    offerEnd: ensureString(
      safeProduct.offerEnd ?? safeProduct.ofertaFim ?? safeProduct.dataFimOferta
    ),
    new: ensureBoolean(safeProduct.new ?? safeProduct.novo, false),
    rating: ensureNumber(safeProduct.rating ?? safeProduct.avaliacao, 0),
    saleCount: ensureNumber(
      safeProduct.saleCount ?? safeProduct.totalVendas ?? safeProduct.vendas,
      0
    ),
    category: [categoryName],
    tag: tagNames.length ? tagNames : [""],
    brand: [brandName],
    variation: normalizeVariations(
      safeProduct.variation ?? safeProduct.variacao ?? safeProduct.variacoes,
      brandName,
      normalizedImages[0] || DEFAULT_IMAGE
    ),
    image: normalizedImages,
    shortDescription: ensureString(
      safeProduct.shortDescription ??
        safeProduct.descricaoCurta ??
        safeProduct.resumo ??
        safeProduct.descricao
    ),
    fullDescription: ensureString(
      safeProduct.fullDescription ??
        safeProduct.descricaoLonga ??
        safeProduct.descricaoCompleta ??
        safeProduct.descricao
    ),
    stock: 50,
  };
};

export const convertFirebaseProducts = (
  products = [],
  { categorias = [], marcas = [], tags = [] } = {}
) => {
  if (Array.isArray(products)) {
    return products.map((product) =>
      convertFirebaseProduct(product, { categorias, marcas, tags })
    );
  }

  if (isObject(products)) {
    return [convertFirebaseProduct(products, { categorias, marcas, tags })];
  }

  return [];
};

export default convertFirebaseProducts;